import "./window";

/**
 * Thrown when a property is read on a config proxy whose backing window
 * global is not defined. Indicates either that `src/config/env.js` failed
 * to load before the first access, or that a test environment did not
 * seed the global. The error message names the missing global.
 */
export class MissingConfigGlobalError extends Error {
  constructor(globalName: string) {
    super(
      `[@portal/shared/config] window.${globalName} is not defined. ` +
        `Ensure src/config/env.js is loaded before any code that reads config.`
    );
    this.name = "MissingConfigGlobalError";
  }
}

/**
 * Thrown on any attempted write (assignment, defineProperty, or delete) on
 * a config proxy. The library is intentionally read-only at runtime —
 * env.js is the only writer of `window.*Config`. Without these traps a
 * write like `appConfig.API_URL = "x"` would land silently on the empty
 * proxy target while subsequent reads still came from window.AppConfig,
 * losing the write with no signal.
 */
export class ReadonlyConfigError extends Error {
  constructor(globalName: string, prop: string | symbol, op: string) {
    super(
      `[@portal/shared/config] cannot ${op} window.${globalName}.${String(prop)}: ` +
        `config is read-only. Update env.js or the test seed instead.`
    );
    this.name = "ReadonlyConfigError";
  }
}

type WindowConfigKey =
  | "AppConfig"
  | "IntegrationsAppConfig"
  | "DataHistoryAppConfig"
  | "SyncHealthConfig"
  | "AssessConfig";

/**
 * Returns a typed Proxy that reads its values from `window[globalName]`
 * lazily on every property access. The proxy can be created before
 * `env.js` runs; reads only fail if the global is still missing at the
 * moment of access.
 *
 * Reading any property on the returned proxy throws
 * {@link MissingConfigGlobalError} when `window[globalName]` is
 * `undefined`. In non-production builds, reading a property that exists
 * on the global but holds `undefined` logs a warning to the console; this
 * branch is tree-shaken in production. The warning is deduped per key
 * per proxy so a hot path reading a missing key does not spam the console.
 *
 * Writes are rejected — see {@link ReadonlyConfigError}.
 *
 * @typeParam T The TypeScript interface describing the global's shape.
 * @param globalName Name of the `window.*Config` global to wrap.
 */
export function makeConfigProxy<T extends object>(
  globalName: WindowConfigKey
): T {
  const warnedKeys = new Set<string>();
  return new Proxy({} as T, {
    /**
     * Returns `window[globalName][prop]` on every read, after asserting
     * the global is defined. Symbol-keyed reads (used by `JSON.stringify`,
     * `String` coercion, and `Symbol.toPrimitive`) are forwarded to the
     * underlying object unchanged so built-in coercion paths behave the
     * same as a direct read of the global.
     */
    get(_target, prop) {
      const source = window[globalName] as T | undefined;
      if (source === undefined) {
        throw new MissingConfigGlobalError(globalName);
      }
      if (typeof prop === "symbol") {
        return (source as Record<symbol, unknown>)[prop];
      }
      const value = (source as Record<string, unknown>)[prop];
      if (
        process.env.NODE_ENV !== "production" &&
        prop in source &&
        value === undefined &&
        !warnedKeys.has(prop)
      ) {
        warnedKeys.add(prop);
        // eslint-disable-next-line no-console
        console.warn(
          `[@portal/shared/config] window.${globalName}.${prop} is declared but undefined`
        );
      }
      return value;
    },

    /**
     * Implements the `in` operator so that `"API_URL" in appConfig` returns
     * the same boolean as `"API_URL" in window.AppConfig`. Returns `false`
     * rather than throwing when the global is missing — `in` is the idiom
     * callers reach for to feature-detect against unseeded test environments,
     * so a missing global should report "no key" rather than blow up.
     */
    has(_target, prop) {
      const source = window[globalName];
      if (source === undefined) {
        return false;
      }
      return prop in source;
    },

    /**
     * Forwards `Reflect.ownKeys` to the backing global so that
     * `Object.keys(appConfig)`, `Object.entries(appConfig)`,
     * `JSON.stringify(appConfig)`, and `{ ...appConfig }` see the same
     * keys as a direct read of `window.AppConfig`. Without this trap the
     * proxy would report no own keys.
     */
    ownKeys() {
      const source = window[globalName];
      if (source === undefined) {
        throw new MissingConfigGlobalError(globalName);
      }
      return Reflect.ownKeys(source);
    },

    /**
     * Pairs with `ownKeys`. `Object.keys` and friends walk the list
     * returned by `ownKeys` but skip any key whose descriptor reports
     * `enumerable: false`. Forwarding the descriptor from the backing
     * global preserves each property's actual enumerability flag.
     */
    getOwnPropertyDescriptor(_target, prop) {
      const source = window[globalName];
      if (source === undefined) {
        throw new MissingConfigGlobalError(globalName);
      }
      return Reflect.getOwnPropertyDescriptor(source, prop);
    },

    set(_target, prop) {
      throw new ReadonlyConfigError(globalName, prop, "assign to");
    },

    defineProperty(_target, prop) {
      throw new ReadonlyConfigError(globalName, prop, "defineProperty on");
    },

    deleteProperty(_target, prop) {
      throw new ReadonlyConfigError(globalName, prop, "delete");
    },
  });
}
